import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader, Home, ChevronRight, ChevronLeft } from 'lucide-react';
import {
  createThaiArrivalCard,
  getThaiArrivalCardStatus,
} from '../../services/thaiArrivalCardService';
import {
  COUNTRIES,
  CHINA_CITIES,
  THAILAND_PROVINCES,
  TRAVEL_PURPOSES,
  ACCOMMODATION_TYPES,
  TRANSPORT_OPTIONS_BY_MODE,
  OCCUPATION_OPTIONS,
  GENDER_OPTIONS,
  TRAVEL_MODES,
} from './thaiData';
import Footer from '../../components/shared/Footer';

export default function ThailandArrivalCardForm() {
  const navigate = useNavigate();
  const TOTAL_STEPS = 4;
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    personal: {
      family_name: '',
      first_name: '',
      middle_name: '',
      passport_no: '',
      nationality: 'CHN',
      date_of_birth: '',
      occupation: '',
      gender: 'MALE',
      country_of_residence: 'CHN',
      city_of_residence: '',
      country_code: '86',
      phone_no: '',
    },
    trip: {
      arrival_date: '',
      country_boarded: 'CHN',
      purpose_of_travel: 'HOLIDAY',
      purpose_other: '',
      arrival_mode: 'AIR',
      arrival_transport: 'COMMERCIAL FLIGHT',
      arrival_transport_other: '',
      arrival_flight_no: '',
      departure_date: '',
      departure_mode: 'AIR',
      departure_transport: 'COMMERCIAL FLIGHT',
      departure_transport_other: '',
      departure_flight_no: '',
      accommodation_type: 'HOTEL',
      accommodation_type_other: '',
      accommodation_province: 'BANGKOK',
      accommodation_district: '',
      accommodation_sub_district: '',
      accommodation_address: '',
      is_transit: false,
    },
    health: {
      countries_visited: ['CHN'],
      email: '',
      email_confirm: '',
      terms_accepted: false,
    },
  });

  const [taskId, setTaskId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const containsChinese = (value) => /[\u4e00-\u9fff]/.test(String(value || ''));
  const normalizeUpperInput = (value) => String(value || '').replace(/[\u4e00-\u9fff]/g, '').toUpperCase();
  const normalizePhoneInput = (value) => String(value || '').replace(/[^\d+]/g, '');

  const CHINA_PRIORITY_CODES = new Set(['CHN', 'HKG', 'MAC', 'TWN']);
  const getCountryLabelBasic = (country) => (
    CHINA_PRIORITY_CODES.has(country.code) ? country.nameCn : country.displayEn
  );
  const getCountryLabelHealth = (country) => country.nameCn || getCountryLabelBasic(country);

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };

  const parseDateInputLocal = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const [year, month, day] = String(yyyyMmDd).split('-').map((n) => Number(n));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  const today = new Date();
  const arrivalMinDate = formatDateLocal(today);
  const arrivalMaxDate = formatDateLocal(addDays(today, 4));
  const emailTrimmed = formData.health.email.trim();
  const emailConfirmTrimmed = formData.health.email_confirm.trim();
  const emailMismatch = Boolean(emailTrimmed && emailConfirmTrimmed && emailTrimmed !== emailConfirmTrimmed);

  // 处理输入变化
  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNationalityChange = (nextNationality) => {
    setFormData(prev => {
      const shouldSyncResidence = prev.personal.country_of_residence === prev.personal.nationality;
      const shouldSyncBoarded = prev.trip.country_boarded === prev.personal.nationality;

      return {
        ...prev,
        personal: {
          ...prev.personal,
          nationality: nextNationality,
          country_of_residence: shouldSyncResidence ? nextNationality : prev.personal.country_of_residence,
        },
        trip: {
          ...prev.trip,
          country_boarded: shouldSyncBoarded ? nextNationality : prev.trip.country_boarded,
        },
      };
    });
  };

  // 验证表单
  const validateForm = () => {
    const { personal, trip, health } = formData;

    const ensureNoChinese = (label, value) => {
      if (!value) return true;
      if (containsChinese(value)) {
        setError(`${label} 不能包含中文，请使用英文/数字填写`);
        return false;
      }
      return true;
    };

    if (!personal.family_name.trim()) {
      setError('请填写姓氏');
      return false;
    }
    if (!ensureNoChinese('姓氏', personal.family_name)) return false;
    if (!personal.first_name.trim()) {
      setError('请填写名字');
      return false;
    }
    if (!ensureNoChinese('名字', personal.first_name)) return false;
    if (!personal.passport_no.trim()) {
      setError('请填写护照号码');
      return false;
    }
    if (!ensureNoChinese('护照号码', personal.passport_no)) return false;
    if (!personal.date_of_birth) {
      setError('请填写出生日期');
      return false;
    }
    if (!personal.nationality) {
      setError('请选择国籍');
      return false;
    }
    if (!personal.occupation.trim()) {
      setError('请选择职业');
      return false;
    }
    if (!ensureNoChinese('职业', personal.occupation)) return false;
    if (!personal.country_of_residence) {
      setError('请选择居住国');
      return false;
    }
    if (!personal.city_of_residence) {
      setError('请选择居住省份/直辖市');
      return false;
    }
    if (!personal.country_code?.trim()) {
      setError('请填写电话区号');
      return false;
    }
    if (!personal.phone_no?.trim()) {
      setError('请填写电话号码');
      return false;
    }
    if (!trip.arrival_date) {
      setError('请填写抵达日期');
      return false;
    }
    {
      const arrival = parseDateInputLocal(trip.arrival_date);
      const min = parseDateInputLocal(arrivalMinDate);
      const max = parseDateInputLocal(arrivalMaxDate);
      if (!arrival || !min || !max || arrival < min || arrival > max) {
        setError('抵达日期必须在今天至未来 4 天内（含）');
        return false;
      }
    }
    if (!trip.arrival_flight_no.trim()) {
      setError('请填写抵达航班号/车次号');
      return false;
    }
    if (!ensureNoChinese('抵达航班号/车次号', trip.arrival_flight_no)) return false;
    if (!trip.country_boarded) {
      setError('请选择出发国');
      return false;
    }
    if (!trip.purpose_of_travel) {
      setError('请选择旅行目的');
      return false;
    }
    if (trip.purpose_of_travel === 'OTHERS (PLEASE SPECIFY)' && !trip.purpose_other.trim()) {
      setError('请填写旅行目的（其他）');
      return false;
    }
    if (!ensureNoChinese('旅行目的（其他）', trip.purpose_other)) return false;
    if (!trip.arrival_mode) {
      setError('请选择入境出行方式');
      return false;
    }
    if (!trip.arrival_transport) {
      setError('请选择入境交通方式');
      return false;
    }
    if (trip.arrival_transport === 'OTHERS (PLEASE SPECIFY)' && !trip.arrival_transport_other.trim()) {
      setError('请填写入境交通方式（其他）');
      return false;
    }
    if (!ensureNoChinese('入境交通方式（其他）', trip.arrival_transport_other)) return false;

    if (trip.departure_date) {
      {
        const arrival = parseDateInputLocal(trip.arrival_date);
        const departure = parseDateInputLocal(trip.departure_date);
        if (arrival && departure && departure < arrival) {
          setError('计划离开泰国的日期不能早于抵达日期');
          return false;
        }
      }
      if (!trip.departure_flight_no.trim()) {
        setError('请填写离境航班号/车次号');
        return false;
      }
      if (!ensureNoChinese('离境航班号/车次号', trip.departure_flight_no)) return false;
      if (!trip.departure_mode) {
        setError('请选择离境出行方式');
        return false;
      }
      if (!trip.departure_transport) {
        setError('请选择离境交通方式');
        return false;
      }
      if (trip.departure_transport === 'OTHERS (PLEASE SPECIFY)' && !trip.departure_transport_other.trim()) {
        setError('请填写离境交通方式（其他）');
        return false;
      }
      if (!ensureNoChinese('离境交通方式（其他）', trip.departure_transport_other)) return false;
    }

    if (!trip.is_transit) {
      if (!trip.accommodation_type) {
        setError('请选择住宿类型');
        return false;
      }
      if (trip.accommodation_type !== 'HOTEL') {
        setError('目前仅支持住宿类型为酒店（HOTEL）');
        return false;
      }
      if (trip.accommodation_type === 'OTHERS (PLEASE SPECIFY)' && !trip.accommodation_type_other.trim()) {
        setError('请填写住宿类型（其他）');
        return false;
      }
      if (!ensureNoChinese('住宿类型（其他）', trip.accommodation_type_other)) return false;
      if (!trip.accommodation_province) {
        setError('请选择住宿省份');
        return false;
      }
      if (!trip.accommodation_address.trim()) {
        setError('请填写住宿地址');
        return false;
      }
      if (!ensureNoChinese('住宿地址', trip.accommodation_address)) return false;
    }

    if (!health.countries_visited?.length) {
      setError('请选择过去两周访问的国家/地区');
      return false;
    }
    if (!health.email.trim()) {
      setError('请填写邮箱地址');
      return false;
    }
    if (!ensureNoChinese('邮箱地址', health.email)) return false;
    if (!health.email_confirm?.trim()) {
      setError('请再次确认邮箱地址');
      return false;
    }
    if (!ensureNoChinese('确认邮箱地址', health.email_confirm)) return false;
    if (health.email_confirm.trim() !== health.email.trim()) {
      setError('两次输入的邮箱地址不一致，请检查');
      return false;
    }
    if (!health.terms_accepted) {
      setError('请勾选“我确认上述信息的准确性并同意支付 5 个积分”');
      return false;
    }

    setError('');
    return true;
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!formData.trip.departure_date) {
      const ok = window.confirm(
        '提示：未填写“计划离开泰国的日期”。泰国官网有时会校验该字段，可能导致提交失败或耗时更久。\n\n建议填写后再提交。是否仍要继续提交？'
      );
      if (!ok) return;
    }
    if (!validateForm()) return;

    setProcessing(true);
    try {
      const payload = {
        personal: { ...formData.personal },
        trip: { ...formData.trip },
        health: { ...formData.health },
      };
      delete payload.health.email_confirm;
      delete payload.trip.accommodation_district;
      delete payload.trip.accommodation_sub_district;

      const response = await createThaiArrivalCard(payload);
      setTaskId(response.task_id);
    } catch (err) {
      setError(err.message || '提交失败');
      setProcessing(false);
    }
  };

  // 轮询状态
  useEffect(() => {
    if (!taskId) return;

    const interval = setInterval(async () => {
      try {
        const status = await getThaiArrivalCardStatus(taskId);
        if (status.status === 'completed') {
          setResult(status);
          setProcessing(false);
          clearInterval(interval);
        } else if (status.status === 'failed') {
          setError(status.last_error || '处理失败');
          setProcessing(false);
          clearInterval(interval);
        }
      } catch (err) {
        console.error('轮询失败:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [taskId]);

  // 提交后提示用户不要关闭浏览器/刷新页面
  useEffect(() => {
    if (!processing) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [processing]);

  // 成功页面
  if (result) {
    const emailUsed = result.result_snapshot?.email || formData.health.email;
    const referenceNumber = result.result_snapshot?.reference_number;
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900">填写成功！</h1>
              <p className="text-gray-600 mt-2">您的泰国入境卡已完成填写</p>
            </div>

            {referenceNumber && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">泰国入境卡编号（TH Digital Arrival Card No.）：</p>
                <p className="text-2xl font-bold text-blue-600 font-mono break-all">
                  {referenceNumber}
                </p>
              </div>
            )}

	            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
	              <p className="text-sm text-amber-800">
	                <strong>提示：</strong>
	                泰国入境卡在线填写后台不保存任何记录。
	                {referenceNumber ? '请保存或打印上方入境卡编号，入境时出示给泰国移民官员即可。' : '请到邮箱查收入境卡邮件（含垃圾箱），入境时按邮件指引出示即可。'}
	                {emailUsed ? ` 邮箱：${emailUsed}` : ''}
	              </p>
	            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all"
              >
                <Home className="w-4 h-4 inline mr-2" />
                返回首页
              </button>
              <button
                onClick={() => navigate('/home')}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg font-medium transition-all"
              >
                返回用户中心
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 处理中页面
  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Loader className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">处理中...</h2>
          <p className="text-gray-600">正在提交您的入境卡信息，请稍候</p>
          <p className="text-sm text-gray-500 mt-4">这个过程通常需要 5-10 分钟</p>
	          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-6 text-left">
	            <p className="text-sm text-amber-800">
	              <strong>请不要关闭浏览器或刷新页面</strong>，直到页面提示完成并收到邮件。
	            </p>
	            <p className="text-sm text-amber-800 mt-2">
	              <strong>隐私提示：</strong>泰国入境卡在线填写后台不保存任何记录，请在完成后自行保存入境卡编号或邮件回执。
	            </p>
	            {formData.health.email && (
	              <p className="text-sm text-amber-800 mt-2">
	                提交成功后请到邮箱 <span className="font-mono">{formData.health.email}</span> 查收（含垃圾箱）。
	              </p>
	            )}
          </div>
        </div>
      </div>
    );
  }

  // 表单页面
  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* 进度指示器 */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">泰国入境卡在线填写</h1>
            <p className="text-gray-600 mb-6">第 {currentStep + 1} 步 / 共 {TOTAL_STEPS} 步</p>
            <div className="flex gap-2">
              {Array.from({ length: TOTAL_STEPS }, (_, step) => (
                <div
                  key={step}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    step <= currentStep ? 'bg-orange-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

	          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
	            <p className="text-sm text-blue-800">
	              <strong>重要提示：</strong>所有需要手动输入的内容请使用英文大写字母/数字，不要输入中文（下拉选项可中文显示）。如粘贴了中文，系统会自动过滤以避免提交失败。
	            </p>
	          </div>
	
	          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
	            <p className="text-sm text-amber-900">
	              <strong>隐私提示：</strong>泰国入境卡在线填写后台不保存任何记录。提交完成后请务必保存入境卡编号或邮件回执。
	            </p>
	          </div>

	          {/* Step 0: 个人信息 */}
	          {currentStep === 0 && (
	            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b">
                步骤 1: 个人信息
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">姓氏 *</label>
                  <input
                    type="text"
                    value={formData.personal.family_name}
                    onChange={(e) => handleInputChange('personal', 'family_name', normalizeUpperInput(e.target.value))}
                    placeholder="例如：YU"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">名字 *</label>
                  <input
                    type="text"
                    value={formData.personal.first_name}
                    onChange={(e) => handleInputChange('personal', 'first_name', normalizeUpperInput(e.target.value))}
                    placeholder="例如：ZECHENG"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">中间名</label>
                  <input
                    type="text"
                    value={formData.personal.middle_name}
                    onChange={(e) => handleInputChange('personal', 'middle_name', normalizeUpperInput(e.target.value))}
                    placeholder="可选"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">护照号码 *</label>
                  <input
                    type="text"
                    value={formData.personal.passport_no}
                    onChange={(e) => handleInputChange('personal', 'passport_no', normalizeUpperInput(e.target.value))}
                    placeholder="例如：EL1234567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">出生日期 *</label>
                  <input
                    type="date"
                    value={formData.personal.date_of_birth}
                    onChange={(e) => handleInputChange('personal', 'date_of_birth', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">性别</label>
                  <select
                    value={formData.personal.gender}
                    onChange={(e) => handleInputChange('personal', 'gender', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {GENDER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.labelCn}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">国籍 *</label>
                  <select
                    value={formData.personal.nationality}
                    onChange={(e) => handleNationalityChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{getCountryLabelBasic(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">职业 *</label>
                  <select
                    value={formData.personal.occupation}
                    onChange={(e) => handleInputChange('personal', 'occupation', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">请选择</option>
                    {OCCUPATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.labelCn}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">提交时会按英文职业填写到泰国官网表单。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">居住国 *</label>
                  <select
                    value={formData.personal.country_of_residence}
                    onChange={(e) => handleInputChange('personal', 'country_of_residence', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{getCountryLabelBasic(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">居住省份/直辖市 *</label>
                  <select
                    value={formData.personal.city_of_residence}
                    onChange={(e) => handleInputChange('personal', 'city_of_residence', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">请选择</option>
                    {CHINA_CITIES.map(city => (
                      <option key={city.value} value={city.value}>{city.labelCn}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">电话号码 *</label>
                  <input
                    type="tel"
                    value={formData.personal.phone_no}
                    onChange={(e) => handleInputChange('personal', 'phone_no', normalizePhoneInput(e.target.value))}
                    placeholder="例如：18965897489"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: 航班信息 */}
          {currentStep === 1 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b">
                步骤 2: 航班信息
              </h2>

              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">入境信息</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">抵达日期 *</label>
                      <input
                        type="date"
                        value={formData.trip.arrival_date}
                        onChange={(e) => handleInputChange('trip', 'arrival_date', e.target.value)}
                        min={arrivalMinDate}
                        max={arrivalMaxDate}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-2">仅支持今天至未来 4 天内（含）。</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">出发国 *</label>
                      <select
                        value={formData.trip.country_boarded}
                        onChange={(e) => handleInputChange('trip', 'country_boarded', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        {COUNTRIES.map(c => (
                          <option key={c.code} value={c.code}>{getCountryLabelBasic(c)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">旅行目的 *</label>
                      <select
                        value={formData.trip.purpose_of_travel}
                        onChange={(e) => {
                          const next = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            trip: {
                              ...prev.trip,
                              purpose_of_travel: next,
                              purpose_other: next === 'OTHERS (PLEASE SPECIFY)' ? prev.trip.purpose_other : '',
                            },
                          }));
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        {TRAVEL_PURPOSES.map(p => (
                          <option key={p.value} value={p.value}>{p.labelCn}</option>
                        ))}
                      </select>
                    </div>

                    {formData.trip.purpose_of_travel === 'OTHERS (PLEASE SPECIFY)' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">旅行目的（其他） *</label>
                        <input
                          type="text"
                          value={formData.trip.purpose_other}
                          onChange={(e) => handleInputChange('trip', 'purpose_other', normalizeUpperInput(e.target.value))}
                          placeholder="例如：VISIT FRIEND"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">入境方式 *</label>
                      <select
                        value={formData.trip.arrival_mode}
                        onChange={(e) => {
                          const mode = e.target.value;
                          const options = TRANSPORT_OPTIONS_BY_MODE[mode] || TRANSPORT_OPTIONS_BY_MODE.AIR;
                          setFormData(prev => ({
                            ...prev,
                            trip: {
                              ...prev.trip,
                              arrival_mode: mode,
                              arrival_transport: options.includes(prev.trip.arrival_transport) ? prev.trip.arrival_transport : (options[0] || ''),
                              arrival_transport_other: '',
                            },
                          }));
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        {TRAVEL_MODES.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.labelCn}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">入境交通方式 *</label>
                      <select
                        value={formData.trip.arrival_transport}
                        onChange={(e) => {
                          const next = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            trip: {
                              ...prev.trip,
                              arrival_transport: next,
                              arrival_transport_other: next === 'OTHERS (PLEASE SPECIFY)' ? prev.trip.arrival_transport_other : '',
                            },
                          }));
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        {(TRANSPORT_OPTIONS_BY_MODE[formData.trip.arrival_mode] || TRANSPORT_OPTIONS_BY_MODE.AIR).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    {formData.trip.arrival_transport === 'OTHERS (PLEASE SPECIFY)' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">入境交通方式（其他） *</label>
                        <input
                          type="text"
                          value={formData.trip.arrival_transport_other}
                          onChange={(e) => handleInputChange('trip', 'arrival_transport_other', normalizeUpperInput(e.target.value))}
                          placeholder="例如：HELICOPTER"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">抵达航班号/车次号 *</label>
                      <input
                        type="text"
                        value={formData.trip.arrival_flight_no}
                        onChange={(e) => handleInputChange('trip', 'arrival_flight_no', normalizeUpperInput(e.target.value))}
                        placeholder="例如：FD571"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">离境信息（可选）</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">计划离开泰国的日期</label>
                      <input
                        type="date"
                        value={formData.trip.departure_date}
                        onChange={(e) => handleInputChange('trip', 'departure_date', e.target.value)}
                        min={formData.trip.arrival_date || arrivalMinDate}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        建议填写：泰国官网有时会校验该字段，未填写可能导致提交失败或耗时更久。填写后将自动要求填写离境方式/交通方式/航班号。
                      </p>
                    </div>

                    {formData.trip.departure_date && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">离境方式 *</label>
                          <select
                            value={formData.trip.departure_mode}
                            onChange={(e) => {
                              const mode = e.target.value;
                              const options = TRANSPORT_OPTIONS_BY_MODE[mode] || TRANSPORT_OPTIONS_BY_MODE.AIR;
                              setFormData(prev => ({
                                ...prev,
                                trip: {
                                  ...prev.trip,
                                  departure_mode: mode,
                                  departure_transport: options.includes(prev.trip.departure_transport) ? prev.trip.departure_transport : (options[0] || ''),
                                  departure_transport_other: '',
                                },
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          >
                            {TRAVEL_MODES.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.labelCn}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">离境交通方式 *</label>
                          <select
                            value={formData.trip.departure_transport}
                            onChange={(e) => {
                              const next = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                trip: {
                                  ...prev.trip,
                                  departure_transport: next,
                                  departure_transport_other: next === 'OTHERS (PLEASE SPECIFY)' ? prev.trip.departure_transport_other : '',
                                },
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          >
                            {(TRANSPORT_OPTIONS_BY_MODE[formData.trip.departure_mode] || TRANSPORT_OPTIONS_BY_MODE.AIR).map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>

                        {formData.trip.departure_transport === 'OTHERS (PLEASE SPECIFY)' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">离境交通方式（其他） *</label>
                            <input
                              type="text"
                              value={formData.trip.departure_transport_other}
                              onChange={(e) => handleInputChange('trip', 'departure_transport_other', normalizeUpperInput(e.target.value))}
                              placeholder="例如：HELICOPTER"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">离境航班号/车次号 *</label>
                          <input
                            type="text"
                            value={formData.trip.departure_flight_no}
                            onChange={(e) => handleInputChange('trip', 'departure_flight_no', normalizeUpperInput(e.target.value))}
                            placeholder="例如：FD570"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: 住宿信息 */}
          {currentStep === 2 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b">
                步骤 3: 住宿信息
              </h2>

              <div className="space-y-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.trip.is_transit}
                    onChange={(e) => handleInputChange('trip', 'is_transit', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">我是过境乘客，不在泰国停留</span>
                </label>

                {!formData.trip.is_transit && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">住宿类型 *</label>
                      <select
                        value={formData.trip.accommodation_type}
                        onChange={(e) => {
                          const next = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            trip: {
                              ...prev.trip,
                              accommodation_type: next === 'HOTEL' ? next : 'HOTEL',
                              accommodation_type_other: '',
                            },
                          }));
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        {ACCOMMODATION_TYPES.map(t => (
                          <option key={t.value} value={t.value} disabled={t.value !== 'HOTEL'}>{`${t.labelCn}（${t.value}）`}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-2">目前仅支持酒店（HOTEL），其它类型暂不支持。</p>
                    </div>

                    {formData.trip.accommodation_type === 'OTHERS (PLEASE SPECIFY)' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">住宿类型（其他） *</label>
                        <input
                          type="text"
                          value={formData.trip.accommodation_type_other}
                          onChange={(e) => handleInputChange('trip', 'accommodation_type_other', normalizeUpperInput(e.target.value))}
                          placeholder="例如：VILLA"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-2">请使用英文/数字填写。</p>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">省份/府 *</label>
                        <select
                          value={formData.trip.accommodation_province}
                          onChange={(e) => handleInputChange('trip', 'accommodation_province', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          {THAILAND_PROVINCES.map(p => (
                            <option key={p.value} value={p.value}>
                              {p.labelCn ? `${p.labelCn}（${p.value}）` : p.value}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-2">中国游客常用目的地已置顶；其余省份暂以英文显示。</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">District/Area（可选）</label>
                        <input
                          type="text"
                          value={formData.trip.accommodation_district}
                          onChange={(e) => handleInputChange('trip', 'accommodation_district', normalizeUpperInput(e.target.value))}
                          placeholder="建议留空（不会提交）"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sub-District/Sub-Area（可选）</label>
                        <input
                          type="text"
                          value={formData.trip.accommodation_sub_district}
                          onChange={(e) => handleInputChange('trip', 'accommodation_sub_district', normalizeUpperInput(e.target.value))}
                          placeholder="建议留空（不会提交）"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">住宿地址 *</label>
                      <textarea
                        value={formData.trip.accommodation_address}
                        onChange={(e) => handleInputChange('trip', 'accommodation_address', normalizeUpperInput(e.target.value))}
                        placeholder="例如：EST BUILDING 123"
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.trip.accommodation_type === 'HOTEL'
                          ? '选择酒店（HOTEL）时，District/Area 与 Sub-District/Sub-Area 建议留空（可选），系统不会提交这两项。'
                          : '非酒店类型时，泰国官网通常要求选择 District/Area（必要时也需要 Sub-District）。不清楚可留空，系统会自动选择可用项兜底（可能不精确）。'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3: 健康申报与提交 */}
          {currentStep === 3 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b">
                步骤 4: 健康申报与提交
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">过去两周访问的国家/地区 *</label>
                  <select
                    multiple
                    value={formData.health.countries_visited}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleInputChange('health', 'countries_visited', selected);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{getCountryLabelHealth(c)}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">按 Ctrl/Cmd 可多选</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">邮箱地址 *</label>
                  <input
                    type="email"
                    value={formData.health.email}
                    onChange={(e) => handleInputChange('health', 'email', e.target.value.replace(/[\u4e00-\u9fff]/g, ''))}
                    placeholder="用于接收入境卡"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-2">请确认邮箱填写无误，提交成功后请检查收件箱/垃圾箱。</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">确认邮箱地址 *</label>
                  <input
                    type="email"
                    value={formData.health.email_confirm}
                    onChange={(e) => handleInputChange('health', 'email_confirm', e.target.value.replace(/[\u4e00-\u9fff]/g, ''))}
                    placeholder="请再次输入邮箱"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  {emailMismatch ? (
                    <p className="text-xs text-red-600 mt-2">两次输入的邮箱不一致，请检查。</p>
                  ) : (
                    (emailTrimmed && emailConfirmTrimmed) ? <p className="text-xs text-green-600 mt-2">邮箱已一致。</p> : null
                  )}
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-400 p-3">
                  <p className="text-sm text-amber-800">
                    <strong>提交提醒：</strong>点击提交后请不要关闭浏览器或刷新页面，直到页面提示完成并收到邮件。
                  </p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.health.terms_accepted}
                    onChange={(e) => handleInputChange('health', 'terms_accepted', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">我确认上述信息的准确性并同意支付 5 个积分 *</span>
                </label>
              </div>
            </div>
          )}

          {/* 导航按钮 */}
          <div className="flex gap-3 justify-between">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 rounded-lg font-medium transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              上一步
            </button>

            {currentStep < TOTAL_STEPS - 1 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all"
              >
                下一步
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={processing || emailMismatch}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all"
              >
                {processing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    提交中...
                  </>
                ) : (
                  '提交表单'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer className="bg-white" />
    </>
  );
}
